import {
  AtomicBlockUtils,
  ContentBlock,
  convertFromRaw,
  convertToRaw,
  Editor as Draft,
  EditorState,
  Modifier,
  RawDraftContentState,
  RichUtils,
  genKey,
} from 'draft-js';
import { FormikValues, useFormikContext } from 'formik';
import React, { useEffect, useRef, useState } from 'react';

import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import Icone from 'Component/Icone';
import Modal from 'Component/Modal';
import { SmartText } from 'Interface/Eva';
import { smartTextSchema } from 'Schema/Eva';
import { NUMBER_OF_PARAGRAPHS } from 'View/constants';
import { useUpload } from 'View/Hook/Aws';
import { useGetSmartTextMutation } from 'View/Service/Default/Eva';

import Ajuda from './Ajuda';
import CaixaDeTexto from './CaixaDeTexto';
import Form from './Form';
import Rotulo from './Rotulo';
import Selecao from './Selecao';

interface Props {
  ajuda?: string;
  name: string;
  rotulo?: string;
  readOnly?: boolean;
  full?: boolean;
}

const Editor = ({
  ajuda,
  name,
  rotulo,
  readOnly,
  full = false,
}: Props): React.ReactElement => {
  const toast = useToast();
  const upload = useUpload();
  const ref = useRef<Draft>(null);
  const [arquivo, setArquivo] = useState<File>();
  const [enviando, setEnviando] = useState<boolean>(false);
  const [modalUpload, setModalUpload] = useState<boolean>(false);
  const [modalSmartText, setModalSmartText] = useState<boolean>(false);
  const [estado, setEstado] = useState(EditorState.createEmpty());
  const { setFieldTouched, getFieldMeta, setFieldValue } =
    useFormikContext<FormikValues>();
  const { touched, error, initialValue, value } = getFieldMeta(name);
  const disabled =
    ref.current?.editor?.closest('fieldset')?.getAttribute('disabled') !== null;

  const [getSmartText, { isLoading: loadingSmartText }] =
    useGetSmartTextMutation();

  const gerarConteudo = (estado: EditorState) => {
    const conteudo = convertToRaw(estado.getCurrentContent());

    setEstado(estado);
    setFieldValue(name, conteudo, true);
    setFieldTouched(name, true, true);
  };

  const negrito = () => {
    gerarConteudo(RichUtils.toggleInlineStyle(estado, 'BOLD'));
  };

  const italico = () => {
    gerarConteudo(RichUtils.toggleInlineStyle(estado, 'ITALIC'));
  };

  const tracado = () => {
    gerarConteudo(RichUtils.toggleInlineStyle(estado, 'UNDERLINE'));
  };

  const riscado = () => {
    gerarConteudo(RichUtils.toggleInlineStyle(estado, 'STRIKETHROUGH'));
  };

  const listaOrdenada = () => {
    gerarConteudo(RichUtils.toggleBlockType(estado, 'ordered-list-item'));
  };

  const listaNaoOrdenada = () => {
    gerarConteudo(RichUtils.toggleBlockType(estado, 'unordered-list-item'));
  };

  const header1 = () => {
    gerarConteudo(RichUtils.toggleBlockType(estado, 'header-one'));
  };

  const header2 = () => {
    gerarConteudo(RichUtils.toggleBlockType(estado, 'header-two'));
  };

  const header3 = () => {
    gerarConteudo(RichUtils.toggleBlockType(estado, 'header-three'));
  };

  const header4 = () => {
    gerarConteudo(RichUtils.toggleBlockType(estado, 'header-four'));
  };

  const header5 = () => {
    gerarConteudo(RichUtils.toggleBlockType(estado, 'header-five'));
  };

  const header6 = () => {
    gerarConteudo(RichUtils.toggleBlockType(estado, 'header-six'));
  };

  const paragrafo = () => {
    gerarConteudo(RichUtils.toggleBlockType(estado, 'paragraph'));
  };

  const quebraDeLinha = () => {
    const contentState = estado.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      'LINEBREAK',
      'MUTABLE'
    );

    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = EditorState.set(estado, {
      currentContent: contentStateWithEntity,
    });

    gerarConteudo(
      AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, genKey())
    );
  };

  const imagem = (url: string) => {
    const contentState = estado.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      'IMAGE',
      'MUTABLE',
      { src: url }
    );

    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = EditorState.set(estado, {
      currentContent: contentStateWithEntity,
    });

    gerarConteudo(
      AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, genKey())
    );
  };

  const mediaBlockRenderer = (block: ContentBlock) => {
    if (block.getType() === 'atomic') {
      return {
        component: Media,
        editable: false,
      };
    }

    return null;
  };

  const Audio = (props: { src: string }) => <audio controls src={props.src} />;

  const Image = (props: { src: string }) => (
    <img src={props.src} className="img-fluid" />
  );

  const Video = (props: { src: string }) => <video controls src={props.src} />;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Media = (props: any) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const entity = props!.contentState!.getEntity(props!.block!.getEntityAt(0));
    const { src } = entity.getData();
    const type = entity.getType();

    let media;
    if (type === 'AUDIO') {
      media = <Audio src={src} />;
    } else if (type === 'IMAGE') {
      media = <Image src={src} />;
    } else if (type === 'VIDEO') {
      media = <Video src={src} />;
    } else if (type === 'LINEBREAK') {
      media = <hr style={{ borderStyle: 'dashed' }} />;
    }

    return media;
  };

  const enviarUpload = async (): Promise<void> => {
    setEnviando(true);

    if (arquivo) {
      const url = await upload('tmp', arquivo);

      imagem(url);
    }

    setEnviando(false);
    setModalUpload(false);
  };

  const requestSmartText = async (data: SmartText): Promise<void> => {
    try {
      const { eva: smartText } = await getSmartText(data).unwrap();

      const selection = estado.getSelection();
      const contentState = estado.getCurrentContent();
      const nextContentState = Modifier.insertText(
        contentState,
        selection,
        smartText
      );

      gerarConteudo(
        EditorState.push(estado, nextContentState, 'insert-characters')
      );

      setModalSmartText(false);
    } catch (error) {
      toast({
        titulo: 'Erro ao gerar o texto',
        conteudo:
          'É provavel que estamos recebendo muitas requisições. Tente novamente mais tarde ou verifique os logs para mais informações',
        tema: 'danger',
      });
    }
  };

  useEffect(() => {
    if (initialValue) {
      const conteudo = convertFromRaw(initialValue as RawDraftContentState);

      gerarConteudo(EditorState.createWithContent(conteudo));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  useEffect(() => {
    if (!value) {
      gerarConteudo(EditorState.createEmpty());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <React.Fragment>
      <div className="form-floating form-floating-group mb-3">
        <div
          className={`form-control h-auto ${error && touched && 'is-invalid'}`}
        >
          <div className="overflow-auto" style={{ maxHeight: 500 }}>
            <Draft
              ref={ref}
              editorState={estado}
              blockRendererFn={mediaBlockRenderer}
              onChange={(estado) => gerarConteudo(estado)}
              readOnly={readOnly || disabled}
              stripPastedStyles={true}
              handleKeyCommand={(command) => {
                const novoEstado = RichUtils.handleKeyCommand(estado, command);

                if (novoEstado) {
                  gerarConteudo(novoEstado);
                  return 'handled';
                }

                return 'not-handled';
              }}
            />
          </div>
          <div className="bg-white mx-n1 border-top pt-2 d-flex flex-wrap justify-content-center justify-content-sm-start gap-1">
            <Botao onClick={negrito} tema="light" icone="type-bold" />
            <Botao onClick={italico} tema="light" icone="type-italic" />
            <Botao onClick={tracado} tema="light" icone="type-underline" />
            <Botao onClick={riscado} tema="light" icone="type-strikethrough" />
            <Botao onClick={listaOrdenada} tema="light" icone="list-ol" />
            <Botao onClick={listaNaoOrdenada} tema="light" icone="list-ul" />
            {full && (
              <>
                <div className="dropdown dropup d-inline-block">
                  <button className="btn btn-light" data-bs-toggle="dropdown">
                    <Icone nome="card-heading" />
                  </button>
                  <ul className="dropdown-menu">
                    <li>
                      <a className="dropdown-item" href="#" onClick={header1}>
                        Cabeçalho 1
                      </a>
                      <a className="dropdown-item" href="#" onClick={header2}>
                        Cabeçalho 2
                      </a>
                      <a className="dropdown-item" href="#" onClick={header3}>
                        Cabeçalho 3
                      </a>
                      <a className="dropdown-item" href="#" onClick={header4}>
                        Cabeçalho 4
                      </a>
                      <a className="dropdown-item" href="#" onClick={header5}>
                        Cabeçalho 5
                      </a>
                      <a className="dropdown-item" href="#" onClick={header6}>
                        Cabeçalho 6
                      </a>
                      <a className="dropdown-item" href="#" onClick={paragrafo}>
                        Corpo
                      </a>
                    </li>
                  </ul>
                </div>
                <Botao
                  onClick={() => setModalSmartText(true)}
                  tema="light"
                  icone="body-text"
                />
                <Botao
                  onClick={quebraDeLinha}
                  tema="light"
                  icone="file-break"
                />
                <Botao
                  onClick={() => setModalUpload(true)}
                  tema="light"
                  icone="image"
                />
              </>
            )}
          </div>
        </div>
        {rotulo && <Rotulo name={name}>{rotulo}</Rotulo>}
        <React.Fragment>{ajuda && <Ajuda>{ajuda}</Ajuda>}</React.Fragment>
        <div className="invalid-tooltip">{error && touched && error}</div>
      </div>
      <Modal
        titulo="Upload de imagem"
        exibir={modalUpload}
        aoFechar={setModalUpload}
      >
        <div className="mb-3">
          <input
            className="form-control"
            type="file"
            id="formFile"
            onChange={(e) => setArquivo(e.target.files?.item(0) as File)}
          />
          <Ajuda>As extenções permitidas são: jpg, png e gif.</Ajuda>
        </div>
        <Botao
          icone="cloud-arrow-up-fill"
          texto="Fazer upload"
          onClick={enviarUpload}
          processando={enviando}
        />
      </Modal>
      <Modal
        titulo="Texto inteligente"
        exibir={modalSmartText}
        aoFechar={setModalSmartText}
      >
        <Form regras={smartTextSchema} onSubmit={requestSmartText}>
          <p>Deixe a EVA criar um texto para você!</p>
          <CaixaDeTexto name="topic" rotulo="Tópico" />
          <Selecao
            name="paragraphs"
            rotulo="Quantidade de parágrafos"
            opcoes={NUMBER_OF_PARAGRAPHS}
          />
          <Botao
            type="submit"
            texto="Criar texto"
            processando={loadingSmartText}
          />
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default Editor;
